import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { httpsCallable } from "firebase/functions";

import { functions } from "../FirebaseConfig";
import { useAuth } from "../lib/auth-context";
import { useViewAsUser } from "../lib/view-as";

/**
 * /claude — natural-language Q&A over AltaJet flight data. Mirrors the
 * cjb-casa Claude chat, adapted to the AltaJet React + Firebase stack:
 * the chat and the admin prompt editor call the `claudeChat` /
 * `claudeChatPrompt` callables (admin-only, gated server-side). The
 * system-prompt template lives in Firestore (config/claudeChat) and is
 * editable from the admin panel at the bottom.
 */

interface ChatMsg {
  role: "user" | "assistant";
  text: string;
}

const EXAMPLES = [
  "¿El Pilatus está ocupado el próximo fin de semana?",
  "¿Cuántos vuelos tiene Daniel este mes?",
];

/** Inline: render **bold** segments; everything else plain text. */
function renderInline(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    const m = /^\*\*([^*]+)\*\*$/.exec(p);
    if (m) {
      return (
        <strong key={i} className="font-semibold text-brand-900">
          {m[1]}
        </strong>
      );
    }
    return <span key={i}>{p}</span>;
  });
}

/**
 * Light markdown layout: turns the assistant's reply into clean blocks —
 * bullets ("- ", indented by leading spaces), numbered items ("1. ") and
 * paragraphs — with inline **bold**. Avoids a full markdown dependency;
 * the prompt keeps Claude to this small subset.
 */
function renderText(text: string) {
  const lines = text.replace(/\r/g, "").split("\n");
  const blocks: React.ReactNode[] = [];
  lines.forEach((line, i) => {
    if (!line.trim()) {
      blocks.push(<div key={i} className="h-2" />);
      return;
    }
    const indentMatch = /^(\s*)/.exec(line);
    const depth = Math.min(Math.floor((indentMatch?.[1].length ?? 0) / 2), 4);
    const trimmed = line.trim();
    const bullet = /^[-•*]\s+(.*)$/.exec(trimmed);
    const numbered = /^(\d+)[.)]\s+(.*)$/.exec(trimmed);
    if (bullet) {
      blocks.push(
        <div key={i} className="flex gap-2" style={{ paddingLeft: `${depth * 0.9 + 0.1}rem` }}>
          <span className="mt-[0.1rem] shrink-0 text-slate-400">•</span>
          <span className="min-w-0">{renderInline(bullet[1])}</span>
        </div>
      );
    } else if (numbered) {
      blocks.push(
        <div key={i} className="flex gap-2" style={{ paddingLeft: `${depth * 0.9}rem` }}>
          <span className="mt-[0.1rem] shrink-0 font-medium text-slate-500">{numbered[1]}.</span>
          <span className="min-w-0">{renderInline(numbered[2])}</span>
        </div>
      );
    } else {
      blocks.push(<p key={i}>{renderInline(trimmed)}</p>);
    }
  });
  return <div className="flex w-full flex-col gap-1">{blocks}</div>;
}

interface SolicitudPrefill {
  af?: string;
  cuenta?: string;
  fecha?: string;
  hora?: string;
  origen?: string;
  destino?: string;
  numpax?: number;
  piloto?: string;
  pasajeros?: string[];
  notas?: string;
  disposicion?: string;
  reposicion?: string;
  /** Optional label for the button (e.g. "Ida" / "Vuelta"); not a form field. */
  titulo?: string;
}

/**
 * Claude proposes flight requests as ```solicitud { ... } ``` JSON blocks
 * (one per leg — a round trip yields two). Pull them all out so we can hide
 * the raw blocks and render a button per leg that deep-links to the prefilled
 * /solicitar form (the user reviews + submits there — Claude never creates
 * the flight itself).
 */
function parseSolicitudes(text: string): { clean: string; solicitudes: SolicitudPrefill[] } {
  const re = /```solicitud\s*([\s\S]*?)```/g;
  const solicitudes: SolicitudPrefill[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    try {
      const obj = JSON.parse(m[1].trim());
      if (obj && typeof obj === "object") solicitudes.push(obj as SolicitudPrefill);
    } catch {
      /* ignore malformed block */
    }
  }
  const clean = text.replace(/```solicitud\s*[\s\S]*?```/g, "").trim();
  return { clean, solicitudes };
}

function solicitudUrl(s: SolicitudPrefill): string {
  const p = new URLSearchParams();
  if (s.af) p.set("af", String(s.af));
  if (s.cuenta) p.set("cuenta", String(s.cuenta));
  if (s.fecha) p.set("fecha", String(s.fecha));
  if (s.hora) p.set("hora", String(s.hora));
  if (s.origen) p.set("origen", String(s.origen));
  if (s.destino) p.set("destino", String(s.destino));
  if (s.numpax != null) p.set("numpax", String(s.numpax));
  if (s.piloto) p.set("piloto", String(s.piloto));
  if (s.disposicion) p.set("disposicion", String(s.disposicion));
  if (s.reposicion) p.set("reposicion", String(s.reposicion));
  if (Array.isArray(s.pasajeros) && s.pasajeros.length) {
    p.set("pasajeros", s.pasajeros.filter(Boolean).join(","));
  }
  if (s.notas) p.set("notas", String(s.notas));
  return "/solicitar?" + p.toString();
}

/**
 * One assistant turn: the reply in a white bubble, and — when Claude
 * proposed flight request(s) — the deep-link button(s) rendered BELOW the
 * bubble (not inside the white box).
 */
function AssistantMessage({ text }: { text: string }) {
  const { clean, solicitudes } = parseSolicitudes(text);
  const multi = solicitudes.length > 1;
  return (
    <div className="flex max-w-[88%] flex-col items-start gap-2 self-start">
      {clean.trim() ? (
        <div className="w-fit rounded-2xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm leading-relaxed text-slate-800">
          {renderText(clean)}
        </div>
      ) : null}
      {solicitudes.map((s, i) => {
        const label = !multi
          ? "Formulario →"
          : s.titulo
            ? `Formulario: ${s.titulo} →`
            : `Formulario ${i + 1} →`;
        return (
          <Link
            key={i}
            to={solicitudUrl(s)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-gold-600 px-4 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-gold-600/20 hover:brightness-110"
          >
            {label}
          </Link>
        );
      })}
    </div>
  );
}

function ThinkingDots() {
  return (
    <span className="inline-flex items-center gap-2 text-sm text-slate-500">
      <svg
        width="22"
        height="22"
        viewBox="-50 -50 100 100"
        aria-hidden
        className="claude-pulse"
      >
        <g fill="#d97757">
          <ellipse rx="4" ry="42" />
          <ellipse rx="4" ry="42" transform="rotate(45)" />
          <ellipse rx="4" ry="42" transform="rotate(90)" />
          <ellipse rx="4" ry="42" transform="rotate(135)" />
        </g>
      </svg>
      pensando…
    </span>
  );
}

/**
 * Human-friendly name for an Anthropic model id, e.g.
 * "claude-sonnet-5" → "Sonnet 5", "claude-opus-4-7" → "Opus 4.7".
 * Falls back to the raw id for shapes it doesn't recognize.
 */
function modelDisplayName(id: string): string {
  const m = id.match(/^claude-([a-z]+)-(\d+)(?:-(\d+))?/);
  if (!m) return id;
  const family = m[1].charAt(0).toUpperCase() + m[1].slice(1);
  return `${family} ${m[2]}${m[3] ? `.${m[3]}` : ""}`;
}

export function Claude() {
  const { usuario } = useAuth();
  const [viewAsUser] = useViewAsUser();
  // The prompt-config panel is super-admin only (Camilo). Hidden for
  // everyone else and while simulating "vista usuario". The callable is
  // also gated super-admin-only server-side.
  const showAdminPanel = !!usuario && usuario.isSuperAdmin && !viewAsUser;
  // Persist the conversation per-user so navigating to the prefilled
  // /solicitar form (and back) doesn't lose the chat — important for the
  // round-trip flow where both leg buttons live in the same message.
  const storageKey = `altajet.claude.chat.${usuario?.id ?? "anon"}`;
  const [messages, setMessages] = useState<ChatMsg[]>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [pregunta, setPregunta] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Model actually used by the backend. Seeded with the current default so
  // the header shows it before the first reply; each response reports the
  // real model (from the callable) and self-corrects this if .env changes.
  const [modelId, setModelId] = useState("claude-sonnet-5");
  const taRef = useRef<HTMLTextAreaElement>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(messages));
    } catch {
      /* storage full / unavailable — non-fatal */
    }
  }, [messages, storageKey]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, busy]);

  function clearChat() {
    setMessages([]);
    setError(null);
    try {
      localStorage.removeItem(storageKey);
    } catch {
      /* ignore */
    }
  }

  function autoSize() {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 160) + "px";
  }

  async function ask(qRaw: string) {
    const q = qRaw.trim();
    if (!q || busy) return;
    setError(null);
    // Snapshot history BEFORE adding the new question (the callable adds it).
    // Cap to the last 16 turns so a long persisted chat doesn't bloat context.
    const historial = messages.slice(-16).map((m) => ({ role: m.role, content: m.text }));
    setMessages((prev) => [...prev, { role: "user", text: q }]);
    setPregunta("");
    setTimeout(autoSize, 0);
    setBusy(true);
    try {
      const call = httpsCallable<
        { pregunta: string; historial: { role: string; content: string }[] },
        { ok: boolean; respuesta: string; usage: unknown; model?: string }
      >(functions, "claudeChat");
      const r = await call({ pregunta: q, historial });
      const respuesta = r.data?.respuesta?.trim() || "(sin respuesta)";
      if (r.data?.model) setModelId(r.data.model);
      setMessages((prev) => [...prev, { role: "assistant", text: respuesta }]);
    } catch (err) {
      const e = err as { message?: string };
      setError(e.message || "No se pudo procesar la pregunta.");
    } finally {
      setBusy(false);
      taRef.current?.focus();
    }
  }

  return (
    <div className="mx-auto max-w-3xl">
      <header className="mb-5">
        <div className="mb-2 h-[3px] w-8 rounded-full bg-brand-700" />
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-brand-900">
              <img src="/claude-logo.svg" alt="" className="h-6 w-6" />
              Claude
              <span className="mt-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium tracking-normal text-slate-500">
                {modelDisplayName(modelId)}
              </span>
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Pregúntame en lenguaje natural sobre los vuelos, aeronaves y
              viajeros de AltaJet.
            </p>
            <p className="mt-1 text-sm text-slate-600">
              Puedes describir una solicitud de vuelo y Claude llena el
              formulario para que lo apruebes.
            </p>
          </div>
          {messages.length > 0 ? (
            <button
              type="button"
              onClick={clearChat}
              className="shrink-0 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-brand-50"
            >
              Nueva conversación
            </button>
          ) : null}
        </div>
      </header>

      {error ? (
        <p className="mb-4 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      ) : null}

      <div className="flex flex-col gap-2.5">
        {messages.map((m, i) =>
          m.role === "assistant" ? (
            <AssistantMessage key={i} text={m.text} />
          ) : (
            <div
              key={i}
              className="max-w-[88%] self-end whitespace-pre-wrap rounded-2xl bg-brand-700 px-3.5 py-2.5 text-sm leading-relaxed text-white"
            >
              {m.text}
            </div>
          )
        )}
        {busy ? (
          <div className="max-w-[88%] self-start rounded-2xl border border-slate-200 bg-white px-3.5 py-2.5">
            <ThinkingDots />
          </div>
        ) : null}
        <div ref={endRef} />
      </div>

      {messages.length === 0 && !busy ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {EXAMPLES.map((ex) => (
            <button
              key={ex}
              type="button"
              onClick={() => {
                setPregunta(ex);
                setTimeout(autoSize, 0);
                taRef.current?.focus();
              }}
              className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-600 hover:border-brand-500 hover:text-brand-700"
            >
              {ex}
            </button>
          ))}
        </div>
      ) : null}

      <div className="mt-3 flex items-end gap-2">
        <textarea
          ref={taRef}
          value={pregunta}
          rows={1}
          onChange={(e) => {
            setPregunta(e.target.value);
            autoSize();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              ask(pregunta);
            }
          }}
          placeholder="Pregúntame algo…"
          className="max-h-40 min-h-[2.7rem] flex-1 resize-none rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
        />
        <button
          type="button"
          disabled={busy || !pregunta.trim()}
          onClick={() => ask(pregunta)}
          className="rounded-lg bg-brand-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-900 disabled:opacity-50"
        >
          Enviar
        </button>
      </div>

      {messages.length > 0 ? (
        <div className="mt-2 text-right">
          <button
            type="button"
            onClick={clearChat}
            className="text-xs font-medium text-slate-500 hover:text-brand-700"
          >
            🗑 Limpiar conversación
          </button>
        </div>
      ) : null}

      {showAdminPanel ? <PromptAdminPanel /> : null}
    </div>
  );
}

interface PromptState {
  prompt: string;
  is_default: boolean;
  default: string;
  placeholders: string[];
}

function PromptAdminPanel() {
  const [loaded, setLoaded] = useState(false);
  const [state, setState] = useState<PromptState | null>(null);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const loadedOnceRef = useRef(false);

  async function load() {
    try {
      const call = httpsCallable<{ op: string }, PromptState & { ok: boolean }>(
        functions,
        "claudeChatPrompt"
      );
      const r = await call({ op: "get" });
      setState(r.data);
      setText(r.data.prompt);
      setLoaded(true);
    } catch (err) {
      const e = err as { message?: string };
      setMsg("Error cargando: " + (e.message || "desconocido"));
    }
  }

  async function save(template: string, label: string) {
    setSaving(true);
    setMsg(label === "reset" ? "Restaurando…" : "Guardando…");
    try {
      const call = httpsCallable<
        { op: string; template: string },
        PromptState & { ok: boolean }
      >(functions, "claudeChatPrompt");
      const r = await call({ op: "save", template });
      setState(r.data);
      setText(r.data.prompt);
      setMsg(label === "reset" ? "✓ Restaurado" : "✓ Guardado");
      setTimeout(() => setMsg(""), 2500);
    } catch (err) {
      const e = err as { message?: string };
      setMsg("Error: " + (e.message || "desconocido"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <details
      className="mt-8 border-t border-slate-200 pt-4"
      onToggle={(e) => {
        if ((e.target as HTMLDetailsElement).open && !loadedOnceRef.current) {
          loadedOnceRef.current = true;
          load();
        }
      }}
    >
      <summary className="cursor-pointer select-none text-sm font-medium text-slate-600 hover:text-brand-700">
        ⚙️ Configurar prompt (admin)
      </summary>
      <div className="mt-3">
        <p className="text-xs text-slate-500">
          Este es el prompt exacto que recibe Claude en cada pregunta. Los
          datos se inyectan al runtime con estos placeholders:
        </p>
        <p className="mt-2 flex flex-wrap gap-1.5">
          {(state?.placeholders ?? []).map((p) => (
            <span
              key={p}
              className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[11px] text-slate-600"
            >
              {p}
            </span>
          ))}
        </p>
        <p className="mt-2 text-xs text-slate-500">
          {!loaded
            ? "Cargando…"
            : state?.is_default
              ? "Usando el prompt por defecto."
              : "Usando un prompt personalizado guardado."}
        </p>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={loaded ? "" : "Cargando…"}
          className="mt-2 block min-h-[18rem] w-full resize-y rounded-md border border-slate-300 px-3 py-2 font-mono text-xs leading-relaxed focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
        />
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={saving || !loaded}
            onClick={() => save(text, "save")}
            className="rounded-md bg-brand-700 px-3.5 py-2 text-sm font-medium text-white hover:bg-brand-900 disabled:opacity-50"
          >
            Guardar
          </button>
          <button
            type="button"
            disabled={saving || !loaded}
            onClick={() => {
              if (
                window.confirm(
                  "¿Restaurar el prompt por defecto? Se perderá el personalizado."
                )
              ) {
                save("", "reset");
              }
            }}
            className="rounded-md border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-brand-50 disabled:opacity-50"
          >
            Restaurar al default
          </button>
          <span className="text-xs text-slate-500">{msg}</span>
        </div>
      </div>
    </details>
  );
}
